<template>
    <div class="SelectView u-InputMarginView"
        :class="[modes.map((m) => `SelectView--${m}`), { 'u-ErrorView': hasError }]">
        <label class="SelectViewLabel" v-if="!!title">{{ title }}</label>
        <v-select class="SelectViewInput" :class="{ 'SelectViewInput--error': isError }" :value="selected"
            v-on:input="input" :options="options" :placeholder="placeholder" :max-height="maxHeight"
            :clearable="clearable" :searchable="searchable" :multiple="multiple" :filterBy="filterBy" ref="select"
            label="id">

            <template slot="option" slot-scope="option">
                <span v-if="lang !== ''" class="SelectViewOption SelectViewOption--selected">
                    <img v-if="isImage && option.translations[lang].image" class="SelectViewOptionImage"
                        :src="option.translations[lang].image.w100Url" />
                    <div v-if="isImage && !option.translations[lang].image" class="SelectViewOptionImage"></div>
                    {{ isSpecialist ?
            `${option.translations[lang].title} ${option.translations[lang].surname}` :
            option.translations[lang].title }}
                </span>
                <span v-else class="SelectViewOption SelectViewOption--selected">
                    {{ option.title }}
                </span>
            </template>
            <template slot="selected-option" slot-scope="option">
                <span v-if="option.id && lang !== ''" class="SelectViewOption SelectViewOption--option">
                    <img v-if="isImage && option.translations[lang].image" class="SelectViewOptionImage"
                        :src="option.translations[lang].image.w100Url" />
                    <div v-if="isImage && !option.translations[lang].image" class="SelectViewOptionImage"></div>
                    {{ isSpecialist ?
            `${option.translations[lang].title} ${option.translations[lang].surname}` :
            option.translations[lang].title }}
                </span>
                <span v-if="option.id && lang === ''" class="SelectViewOption SelectViewOption--option">
                    {{ option.id }}
                </span>
                <span v-if="!option.id && !isOpenMenu && searchValue.length === 0"
                    class="SelectViewOption SelectViewOption--option">
                    {{ placeholder }}
                </span>
            </template>
        </v-select>
        <svg-view :class="{ open: isOpenMenu }" name="arrow" />
        <button class="SelectViewEdit" v-if="!!editOptionsTitle" @click="onEditOptions">{{ editOptionsTitle }}</button>
    </div>
</template>

<script>
import vSelect from 'vue-select';
import isArray from 'lodash/isArray';

export default {
    name: "SelectView",
    mixins: [mixins.ValidationComponentMixin],
    components: {
        vSelect
    },
    props: {
        title: {
            type: String,
            default: ''
        },
        editOptionsTitle: {
            type: String,
            default: ''
        },
        onEditOptions: {
            type: Function,
            default: () => { }
        },
        value: {
            type: [Object, Number, String, Array],
            default: ''
        },
        options: {
            type: Array,
            default: () => []
        },
        placeholder: {
            type: String,
            default: ''
        },
        modes: {
            type: Array,
            default: () => []
        },
        maxHeight: {
            type: String,
            default: '40px'
        },
        clearable: {
            type: Boolean,
            default: false
        },
        searchable: {
            type: Boolean,
            default: true
        },
        multiple: {
            type: Boolean,
            default: false
        },
        preserveValue: {
            type: Boolean,
            default: true
        },
        lang: {
            type: String,
            default: ''
        },
        isImage: {
            type: Boolean,
            default: true
        },
        isError: {
            type: Boolean,
            default: false
        },
        isCustom: {
            type: Boolean,
            default: false
        },
        isSpecialist: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            isOpenMenu: false,
            searchValue: ''
        }
    },
    computed: {
        selected() {
            if (this.preserveValue) {
                return this.innerValue;
            }

            const val = this.multiple && !isArray(this.innerValue) ? this.innerValue ? [this.innerValue] : [] : this.innerValue;
            const vv = this.multiple
                ? this.options.filter((v) => val.indexOf(v.id) !== -1)
                : this.options.find((v) => v.id == val)
            return vv
        }
    },
    mounted() {
        this.value && this.value.$touch && this.value.$touch();

        const observer = new MutationObserver((mutationsList) => {
            for (let mutation of mutationsList) {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    this.isOpenMenu = !this.isOpenMenu
                }
            }
        });

        observer.observe(this.$refs.select.$el, { attributes: true });
    },
    methods: {
        input($event) {
            if (this.preserveValue) {
                if (this.lang === '') {
                    this.innerValue = $event.title
                    return;
                }

                if (this.isCustom === true) {
                    this.innerValue = $event;
                } else {
                    for (const key in $event) {
                        this.innerValue[key] = $event[key];
                    }
                }
                return;
            }

            for (const key in $event) {
                Object.assign(this.innerValue, { [key]: $event[key] });
            }

            const m = (input) => input && typeof input.id !== 'undefined' ? input.id : input;
            this.innerValue = isArray($event) ? $event.map(m) : m($event);

            if ($event === null) {
                this.$emit('clear');
                return;
            }
        },
        filterBy(option, label, search) {
            this.searchValue = search

            if (this.lang !== '') {
                if (this.isSpecialist) {
                    const searchValue = `${option.translations[this.lang].title} ${option.translations[this.lang].surname}`;
                    return (searchValue || '').toLowerCase().indexOf(search.toLowerCase()) === 0;
                } else {
                    return (option.translations[this.lang].title || '').toLowerCase().indexOf(search.toLowerCase()) === 0;
                }
            } else {
                return (option.title || '').toLowerCase().indexOf(search.toLowerCase()) === 0;
            }
        }
    }
};
</script>

<style scoped lang="less">
@import "~styles/const.less";

@r: .SelectView;

@{r} {
    position: relative;
    width: 100%;
    max-height: 66px;

    &Label {
        display: block;
        white-space: nowrap;
        font-size: 12px;
        line-height: 16px;
        color: @color-black;
    }

    &Input {
        padding: 0 15px;
        background: white;
        border: 1px solid #B3B3B3;
        height: 40px;

        :deep(.vs__dropdown-menu) {
            overflow: auto !important;
            padding: 0;
            border-radius: 0;
            box-shadow: none;
            max-height: 280px;
            position: absolute;
            margin-top: 1px;
            left: 0;
            right: 0;
            background-color: @color-white;
            z-index: 55;
        }

        :deep(.vs__dropdown-option.vs__dropdown-option--highlight) {
            cursor: pointer;

            &:hover {
                opacity: .7;
            }
        }

        :deep(.vs__dropdown-option) {
            margin: 0 !important;

            &::marker {
                display: none;
                content: none;
            }
        }

        :deep(.vs__dropdown-menu li[role=option]) {
            font-weight: @medium;
            font-size: 18px;
            line-height: 40px;
            margin-left: 30px;

            a {
                max-width: 100%;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                display: block;
                padding: 0 10px;
            }
        }

        :deep(.vs__dropdown-toggle) {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: space-between;
        }

        :deep(.clear) {
            font-size: 20px !important;
            font-weight: normal !important;
            color: @color-link;
            margin-top: 4px;
            display: none;
        }

        :deep(.dropdown-toggle) {
            border: 1px solid @color-border-gray;
            border-radius: 0;
            padding: 1px 4px 7px;
            font-weight: @medium;
            font-size: 16px;
            line-height: 30px;
            letter-spacing: -0.028em;
        }

        :deep(.dropdown-menu) {
            overflow: auto !important;
            padding: 0;
            border-radius: 0;
            border-color: @color-border-gray;
            box-shadow: none;
        }

        :deep(.dropdown-menu li[role=option]) {
            font-weight: @medium;
            font-size: 16px;
            line-height: 40px;
            letter-spacing: -0.028em;

            a {
                max-width: 100%;
                text-overflow: ellipsis;
                overflow: hidden;
                white-space: nowrap;
                display: block;
                padding: 0 10px;
            }
        }

        :deep(.dropdown-menu .active > a) {
            background: none;
            color: @color-text-gray;
        }

        :deep(.dropdown-menu > .highlight > a) {
            background: none;
            color: @color-red;
        }

        :deep(.open-indicator:before) {
            width: 0;
            height: 0;
            border: 4px solid;
            border-color: black transparent transparent transparent;
            position: relative;
            top: -2px;
            transform: none !important;
        }

        :deep(.open-indicator) {
            display: inline-block;
            padding: 0;
            margin: 14px 5px 0 0;
            width: 8px;
            height: 8px;
        }

        &.open :deep(.open-indicator) {
            transform: rotate(180deg);

        }

        :deep(.no-options) {
            font-weight: normal;
            font-size: 12px;
            line-height: 30px;
            letter-spacing: 0;
            color: @color-light-text;
        }

        :deep(.selected-tag) {
            max-width: calc(100% - 20px);
            font-weight: @medium;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
            display: inline-block;
            margin-left: 0;
        }

        :deep(.vs__selected) {
            flex-shrink: 0;
            padding-right: 5px;
        }

        :deep(.vs__selected-options) {
            display: flex;
            align-items: center;
            flex-wrap: nowrap;
            width: 100%;
        }

        :deep(input[type=search]) {
            width: 100%;
            border: none;
            outline: none;
            padding: 11px 0;
            background-image: none !important;

            &::placeholder {
                color: black;
            }

            &::-webkit-search-decoration,
            &::-webkit-search-cancel-button,
            &::-webkit-search-results-button,
            &::-webkit-search-results-decoration {
                display: none;
            }
        }

        :deep(.vs__open-indicator) {
            display: none;
        }

        &--error {
            border-color: @color-red;
        }
    }

    &Edit {
        position: absolute;
        top: 3px;
        right: 0;
        font: 12px/30px;
        color: @color-red;
        transition: opacity 0.2s;
        cursor: pointer;

        &:hover {
            opacity: 0.7;
        }
    }

    &Option {
        display: flex;
        align-items: center;
        pointer-events: none;
        padding: 10px 15px;
        font-size: 12px;
        line-height: 16px;
        border-right: 1px solid #E6E6E6;
        border-bottom: 1px solid #E6E6E6;
        border-left: 1px solid #E6E6E6;

        &--option {
            padding: 0;
            border: none;
            max-width: 375px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }

        &Image {
            width: 20px;
            height: 20px;
            border-radius: 100%;
            overflow: hidden;
            margin-right: 10px;
            background-color: #B3B3B3;
            object-fit: cover;
            object-position: bottom;
        }
    }

    &--inline {
        @{r}Input {
            :deep(.dropdown-toggle) {
                border: none;
            }

            :deep(.selected-tag) {
                color: @color-red;
            }

            :deep(.open-indicator:before) {
                border-color: @color-red transparent transparent transparent;
            }
        }

    }

    .svg--arrow {
        position: absolute;
        right: 15px;
        bottom: 15px;
        width: 15px;
        height: 8px;
        pointer-events: none;
        transition: transform .3s ease;

        &.open {
            transform: rotate(180deg);
        }
    }
}

.SelectViewLabel+.SelectViewInput {
    margin: 10px 0 0;
}

.u-ShowErrors .SelectView.u-ErrorView .SelectViewInput :deep(.dropdown-toggle) {
    border-color: @color-red !important;

}
</style>
